import {
  Accent,
  Box,
  Heading,
  Image,
  NeoColor,
  P2,
  P3,
  P4,
  Stack,
} from "@noom/wax-component-library";
import type { ReactNode } from "react";

export type TreatmentProductLockupVariant =
  | "treatmentName"
  | "doseSummary"
  | "cadence"
  | "directions"
  | "labResults";

export type TreatmentProductLockupDirectionLine = {
  label: string;
  text: string;
  /**
   * `inline` (default) keeps label and text on one row, wrapping as needed
   * ("Morning: 2 AM capsules"). `stacked` breaks after the label so the text
   * always starts a new row, regardless of how wide the box is.
   */
  layout?: "inline" | "stacked";
};

type TreatmentProductLockupProps = {
  variant?: TreatmentProductLockupVariant;
  eyebrow?: ReactNode;
  /** Omitted on treatment detail when the name is shown above the lockup (Care card uses P2). */
  title?: string;
  subtitle?: string;
  /** Supplement dosing lines for the beige info box (Figma Med header). */
  directionLines?: TreatmentProductLockupDirectionLine[];
  imageUrl?: string | null;
  imageAlt?: string;
  productBackgroundColor?: string | null;
};

const LOCKUP_BAND_MIN_HEIGHT = "56px";
const PRODUCT_IMAGE_SLOT_WIDTH = "100px";
const PRODUCT_IMAGE_SLOT_HEIGHT = "130px";
const LAB_RESULTS_BAND_HEIGHT = "100px";
const LAB_RESULTS_BAND_RADIUS = "12px";

export const TreatmentProductLockup = ({
  variant = "treatmentName",
  eyebrow,
  title,
  subtitle,
  directionLines,
  imageUrl,
  imageAlt,
  productBackgroundColor,
}: TreatmentProductLockupProps) => {
  const bandBg = productBackgroundColor ?? NeoColor.blue.sky;
  const imageAltText =
    imageAlt ?? title ?? (typeof eyebrow === "string" ? eyebrow : "");
  const hasImage = !!imageUrl;
  const isLabResults = variant === "labResults";

  const textContent =
    variant === "directions" ? (
      <Stack spacing={1} flex="1" minWidth={0}>
        {(directionLines ?? []).map((line) => {
          const stacked = line.layout === "stacked";
          return (
            <P2
              key={line.label}
              fontWeight="normal"
              color={NeoColor.greyscale.black}
            >
              <Box
                as="span"
                fontWeight="medium"
                display={stacked ? "block" : "inline"}
              >
                {line.label}
              </Box>
              {/* Space separates label+text for a11y; collapses after a block label. */}
              {` ${line.text}`}
            </P2>
          );
        })}
      </Stack>
    ) : variant === "cadence" ? (
      <Stack flex="1" minWidth={0}>
        {title ? <P2 fontWeight="normal">{title}</P2> : null}
      </Stack>
    ) : variant === "labResults" ? (
      <Stack spacing={1} flex="1" minWidth={0}>
        {title ? (
          <P3 fontWeight="medium" color={NeoColor.greyscale.black}>
            {title}
          </P3>
        ) : null}
        {subtitle && (
          <P4 fontWeight="normal" color={NeoColor.greyscale.black}>
            {subtitle}
          </P4>
        )}
      </Stack>
    ) : variant === "doseSummary" ? (
      <Stack spacing={2} flex="1" minWidth={0}>
        <Box display="flex" alignItems="baseline">
          <Heading as="span" size="md" fontWeight="normal">
            {title}
          </Heading>
          {eyebrow && (
            <P4 ml={2} fontWeight="normal">
              {eyebrow}
            </P4>
          )}
        </Box>
        {subtitle && <P3 fontWeight="normal">{subtitle}</P3>}
      </Stack>
    ) : (
      <Stack spacing={1} flex="1" minWidth={0} lineHeight={1}>
        {eyebrow && (
          <Accent
            size="xs"
            fontWeight="medium"
            color={NeoColor.greyscale.gray3}
          >
            {eyebrow}
          </Accent>
        )}
        {title ? <P3 fontWeight="medium">{title}</P3> : null}
      </Stack>
    );

  return (
    <Box
      width="100%"
      minHeight={hasImage ? PRODUCT_IMAGE_SLOT_HEIGHT : LOCKUP_BAND_MIN_HEIGHT}
      position="relative"
      display="flex"
      alignItems={isLabResults && hasImage ? "flex-end" : "center"}
    >
      <Box
        width="100%"
        minHeight={
          isLabResults ? LAB_RESULTS_BAND_HEIGHT : LOCKUP_BAND_MIN_HEIGHT
        }
        borderRadius={isLabResults ? LAB_RESULTS_BAND_RADIUS : "10px"}
        display="flex"
        alignItems="center"
        bg={bandBg}
      >
        <Box
          flex="1"
          minWidth={0}
          display="flex"
          alignItems="center"
          pl={4}
          pr={hasImage ? PRODUCT_IMAGE_SLOT_WIDTH : 4}
          py={3}
        >
          {textContent}
        </Box>
      </Box>
      {hasImage && (
        <Box
          position="absolute"
          right={0}
          top="50%"
          transform="translateY(-50%)"
          width={PRODUCT_IMAGE_SLOT_WIDTH}
          height={PRODUCT_IMAGE_SLOT_HEIGHT}
          display="flex"
          alignItems="center"
          justifyContent="center"
          lineHeight={0}
        >
          <Image
            src={imageUrl}
            alt={imageAltText}
            display="block"
            width={PRODUCT_IMAGE_SLOT_WIDTH}
            height={PRODUCT_IMAGE_SLOT_HEIGHT}
            objectFit="contain"
            objectPosition="center"
          />
        </Box>
      )}
    </Box>
  );
};
